'use client';
import { Check, DoneAll, Notes, School } from '@mui/icons-material';
import { Box, Chip, Typography } from '@mui/material';
import TooltipModal from '@/components/shared/tooltip-modal';
import { useCourses } from '@/providers';
import { useViewStore } from '@/stores';
import type { Chapter } from '@/types';
import { useRoute } from '@/utils/client/route';

const sxRoot = {
  background: 'none',
  width: '100%',
  textAlign: 'left',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  color: 'text.primary',
  userSelect: 'none',
  border: 0,
  cursor: 'pointer',
  p: '4px',
  '&:focus, &:hover': {
    backgroundColor: 'action.hover',
  },
  '&.compact': {
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '&.wide': {
    px: '14px',
  },
  '&.active': {
    backgroundColor: 'action.selected',
  },
};

export default function CurriculumItem({
  chapter,
  hasMembership,
  indentation = true,
}: {
  chapter: Chapter;
  hasMembership: boolean;
  indentation?: boolean;
}) {
  const { goToRoute } = useRoute();
  const { selectedCourse, selectedChapter, courseStatus } = useCourses();
  const { toggleMenuDrawer, showMenuDrawer } = useViewStore();
  const completed = courseStatus?.chapterStatuses?.find(
    ({ chapter: chapterId, chapterType }) =>
      chapter.item?.id === chapterId && chapterType === chapter.type,
  )?.completed;
  const correct = !!courseStatus?.chapterStatuses?.find(
    ({ chapter: chapterId, chapterType, questionsStatistics }) =>
      chapter.item?.id === chapterId &&
      chapterType === 'Exercise' &&
      questionsStatistics?.allCorrect,
  );
  const isActive = () => selectedChapter?.id === chapter.item?._id;
  const {
    indent,
    item: { access, title, slug },
    type,
    prefix,
    style,
  } = chapter;

  const onNavigate = () => {
    if (type !== 'ChapterSection' && selectedCourse?.slug && slug) {
      goToRoute('myCourseLearn', {
        courseSlug: selectedCourse.slug,
        chapterSlug: slug,
      });

      if (showMenuDrawer) toggleMenuDrawer();
    }
  };

  const getTypeIcon = () => {
    if (type === 'Exercise')
      return (
        <TooltipModal title="Übung">
          <School />
        </TooltipModal>
      );
    if (type === 'Unit')
      return (
        <TooltipModal title="Lektion">
          <Notes />
        </TooltipModal>
      );
    return null;
  };

  const getClassString = () => {
    const classes = [
      style,
      'labelRoot',
      access === 'membership' && !hasMembership ? 'needs-purchase' : '',
    ];
    if (indentation) classes.push(`indent indent-${indent}`);
    return classes.join(' ');
  };

  return (
    <Box
      component="button"
      onClick={onNavigate}
      className={`${indentation ? 'wide' : 'compact'} ${isActive() ? 'active' : ''}`}
      sx={sxRoot}>
      <Box
        className={getClassString()}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          py: 0.5,
          color: 'inherit',
          '& > div': { display: 'flex' },
          '&.needs-purchase': { color: 'text.disabled' },
          '&.item, &.section': {
            fontSize: (theme) => theme.typography.body2.fontSize,
          },
          '&.h1': { fontSize: (theme) => theme.typography.h5.fontSize },
          '&.h2': { fontSize: (theme) => theme.typography.h6.fontSize },
          '&.h3': { fontSize: (theme) => theme.typography.h6.fontSize },
          [theme.breakpoints.up('sm')]: {
            '&.indent-1': { ml: 1.5 },
            '&.indent-2': { ml: 3 },
            '&.indent-3': { ml: 4.5 },
            '&.indent-4': { ml: 6 },
          },
        })}>
        <Box
          sx={{
            mr: 1,
            minWidth: 20,
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            '& .MuiSvgIcon-root': { width: 18, height: 18 },
          }}>
          {getTypeIcon()}
        </Box>
        <Typography
          sx={{
            lineHeight: 1.2,
            fontSize: 'inherit',
            fontWeight: 'inherit',
            flexGrow: 1,
            mr: 1,
            color: 'inherit',
          }}>
          {prefix && (
            <Typography color="gray" component="span" mr={1}>
              {prefix}
            </Typography>
          )}
          {title}
        </Typography>
        <Box>
          {completed && correct && <DoneAll color="success" sx={{ mr: 1 }} />}
          {completed && !correct && <Check sx={{ mr: 1 }} />}
          {!hasMembership && access !== 'membership' && (
            <Chip
              label="kostenlos"
              size="small"
              variant="outlined"
              color="success"
              sx={{ ml: '4px' }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
